import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { I18NextPipe } from 'angular-i18next';
import { AlertComponent, ButtonComponent, MbsPopupType, MbsSize } from 'mbs-ui-kit';
import { HelpLinkPipe } from 'mbs-ui-shared@help/helpLink.pipe';
import { getForceUpdateBuildOsKey } from '../computers-force-update-modal.constants';
import { BuildAction, BuildActionOffer, NewVersionEntry } from '../force-update-new-version-utils/force-update-new-version';

type OfferSentence = {
  offer: BuildActionOffer;
  entries: (NewVersionEntry & { osKey: string })[];
  count: number;
  beforeKey: string;
  afterKey: string;
  linkKey: string;
  suffixKey: string;
  suffixParams: Record<string, unknown>;
};

const newVersionKey = (key: string): string => (key ? `shared:forceUpdate.newVersion.${key}` : null);

const sentenceKeys: Record<BuildAction, { before: string; after: string; link: string }> = {
  [BuildAction.GenerateFirst]: { before: 'generateFirstText', after: null, link: 'generate' },
  [BuildAction.MakePublic]: { before: 'makePublicBefore', after: 'makePublicAfter', link: 'makePublic' },
  [BuildAction.GenerateSandbox]: { before: 'availableBefore', after: 'availableAfter', link: 'generateSandbox' },
  [BuildAction.Generate]: { before: 'availableBefore', after: 'availableAfter', link: 'generate' }
};

const getSuffix = (
  { action, showOs, publicVersion }: BuildActionOffer,
  count: number
): { key: string; params: Record<string, unknown> } => {
  switch (action) {
    case BuildAction.GenerateFirst:
      return { key: 'generateFirstSuffix', params: {} };
    case BuildAction.GenerateSandbox:
      return { key: 'generateSandboxSuffix', params: { count } };
    case BuildAction.MakePublic:
      if (showOs) return { key: 'makePublicGroupSuffix', params: { count } };

      return publicVersion ? { key: 'makePublicSuffix', params: { version: publicVersion } } : { key: 'makePublicFirstSuffix', params: {} };
    default:
      return showOs ? { key: 'generateGroupSuffix', params: { count } } : { key: 'generateSuffix', params: { version: publicVersion } };
  }
};

const toSentence = (offer: BuildActionOffer): OfferSentence => {
  const count = offer.entries.length;
  const keys = sentenceKeys[offer.action];
  const suffix = getSuffix(offer, count);

  return {
    offer,
    entries: offer.entries.map((entry) => ({ ...entry, osKey: getForceUpdateBuildOsKey(entry.buildType, entry.os) })),
    count,
    beforeKey: newVersionKey(keys.before),
    afterKey: newVersionKey(keys.after),
    linkKey: newVersionKey(keys.link),
    suffixKey: newVersionKey(suffix.key),
    suffixParams: suffix.params
  };
};

@Component({
  selector: 'mbs-force-update-new-version-alert',
  templateUrl: './force-update-new-version-alert.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [AlertComponent, ButtonComponent, HelpLinkPipe, I18NextPipe]
})
export class ForceUpdateNewVersionAlertComponent {
  public readonly offers = input.required<BuildActionOffer[]>();
  public readonly showReleaseNotes = input(false);
  public readonly canManageBuilds = input(false);
  public readonly busyActions = input<BuildAction[]>([]);
  public readonly disabled = input(false);
  public readonly actionClick = output<BuildActionOffer>();

  public readonly sentences = computed(() => this.offers().map(toSentence));

  public readonly alertType = MbsPopupType;
  public readonly alertSize = MbsSize.sm;
  public readonly buttonSize = MbsSize.inherit;
  public readonly elementSelectors = {
    name: {
      version: 'force-update-new-version',
      action: 'force-update-action'
    }
  };
}
